import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PersistentCacheService } from '@copado/cds';
import { Subject } from 'rxjs';

import { UserState } from '@app/auth/user.state';
import { CanUseService, Feature } from '@app/shared/can-use.service';

export interface LayoutState {
  hasAnalytics: boolean;
  hasTestAuthoring: boolean;
  hasRoboticExplorer: boolean;
  isCollapsed: boolean;
}

@Injectable()
export class LayoutService {
  protected userState = inject(UserState);
  private canUseService = inject(CanUseService);
  private persistentCacheService = inject(PersistentCacheService);

  private _state = signal<LayoutState>({
    hasAnalytics: false,
    hasRoboticExplorer: false,
    hasTestAuthoring: false,
    isCollapsed: this.persistentCacheService.get('sidebarCollapsed') ?? false,
  });

  private state = this._state.asReadonly();

  readonly isCollapsed = computed(() => this.state().isCollapsed);
  readonly hasTestAuthoring = computed(() => this.state().hasTestAuthoring);
  readonly hasRoboticExplorer = computed(() => this.state().hasRoboticExplorer);

  readonly toggleSidebar$ = new Subject<'expand' | 'switch' | 'collapse'>();

  constructor() {
    this.toggleSidebar$.pipe(takeUntilDestroyed()).subscribe((toggle) => {
      this._state.update((state) => ({
        ...state,
        isCollapsed: { collapse: true, expand: false, switch: !state.isCollapsed }[toggle],
      }));
    });

    effect(() => {
      this.persistentCacheService.set('sidebarCollapsed', this.isCollapsed());
    });

    effect(() => {
      this.userState.org();
      untracked(() => {
        this._state.update((state) => ({
          ...state,
          hasAnalytics: this.canUseService.can(Feature.Analytics),
          hasRoboticExplorer: this.canUseService.can(Feature.Explorer),
          hasTestAuthoring: this.canUseService.can(Feature.TestAuthoring),
        }));
      });
    });
  }
}
